const SCHEME_START_RE = /https?:\/\//gi

const stripTrailingJunk = (value: string): string =>
  value.replace(/[.,;:!?)\]}>]+$/g, '').trim()

const looksLikeBareUrl = (value: string): boolean => {
  if (!value || /\s/.test(value)) return false
  if (/^https?:\/\//i.test(value)) return false
  if (/^(?:t\.me|telegram\.me)\//i.test(value)) return true
  return /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(?:[/:?#].*)?$/i.test(value)
}

const normalizeLinkCandidate = (raw: string): string | null => {
  const trimmed = stripTrailingJunk(raw)
  if (!trimmed) return null

  let candidate = trimmed
  if (!/^https?:\/\//i.test(candidate)) {
    if (!looksLikeBareUrl(candidate)) return null
    candidate = `https://${candidate}`
  }

  try {
    const url = new URL(candidate)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return url.href
  } catch {
    return null
  }
}

/** Pull absolute http(s) URLs in appearance order, including stuck-together ones. */
const extractUrlsWithScheme = (input: string): { urls: string[]; remainder: string } => {
  const starts: number[] = []
  SCHEME_START_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = SCHEME_START_RE.exec(input)) !== null) {
    starts.push(match.index)
  }

  if (starts.length === 0) {
    return { urls: [], remainder: input }
  }

  const urls: string[] = []
  const consumed: Array<[number, number]> = []

  for (let i = 0; i < starts.length; i++) {
    const start = starts[i]
    const limit = i + 1 < starts.length ? starts[i + 1] : input.length
    const rawChunk = input.slice(start, limit)
    const token = stripTrailingJunk(rawChunk.split(/[\s,]+/)[0] ?? '')
    if (!token) continue
    urls.push(token)
    consumed.push([start, start + token.length])
  }

  let remainder = ''
  let cursor = 0
  for (const [from, to] of consumed) {
    if (from > cursor) {
      remainder += input.slice(cursor, from)
    }
    remainder += ' '
    cursor = to
  }
  if (cursor < input.length) {
    remainder += input.slice(cursor)
  }

  return { urls, remainder }
}

/**
 * Extracts ordered unique links from free-form text:
 * - one URL per line
 * - space / comma separated
 * - stuck-together `https://...https://...`
 * - bare domains like `t.me/channel` → `https://t.me/channel`
 */
export const parseExternalLinksInput = (input: string): string[] => {
  if (!input?.trim()) return []

  const seen = new Set<string>()
  const links: string[] = []

  const push = (raw: string) => {
    const normalized = normalizeLinkCandidate(raw)
    if (!normalized) return
    if (seen.has(normalized)) return
    seen.add(normalized)
    links.push(normalized)
  }

  const { urls, remainder } = extractUrlsWithScheme(input)
  for (const url of urls) {
    push(url)
  }

  for (const token of remainder.split(/[\s,]+/)) {
    push(token)
  }

  return links
}

export const formatExternalLinksForInput = (links: string[] | null | undefined): string => {
  if (!Array.isArray(links) || links.length === 0) return ''
  return links.join('\n')
}

/** Pretty-print free-form input as one URL per line (order preserved). */
export const normalizeExternalLinksForDisplay = (input: string): string =>
  formatExternalLinksForInput(parseExternalLinksInput(input))
